import { transient } from "aurelia-dependency-injection";
import { Lookups } from "../../../../../../../../constants/mep/mep-lookup.constants";
import { MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import * as Enumerable from 'linq';
import { MepLookupService } from "../../../../../../../../services/mep/mep-lookup.service";
import { MepLookupFiltersHandler } from "../../../../common-vm/mep-lookup-filters-handler";
import { computedFrom } from "aurelia-binding";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { BaseMepSummaryRequest } from "../../../../../../../../models/mep/mep-summary.model";
import { useView } from "aurelia-framework";

@transient()
@useView('views/pages/modules/mep/tabs/mep-home/partials/mep-ports-summary-filter/mep-ports-summary-filter.html')
export class MepPortsSummaryFilter {

    public isRefreshingData: boolean;

    public selectedEmissionTypes: MepLookupItem[] = [];
    public selectedPorts: MepLookupItem[] = [];
    public selectedVesselTypeGroups: MepLookupItem[] = [];
    public selectedScopes: MepLookupItem[] = [];
    public selectedYear: MepLookupItem = null;
    public selectedOperationModes: MepLookupItem[] = [];
    public financialYear;

    refreshDataFunc: (request) => Promise<string[]>;
    applyFiltersFunc: () => void;

    protected lookupFiltersHandler: MepLookupFiltersHandler;

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.emissionTypes')
    get emissionTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.emissionTypes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.ports')
    get ports(): MepLookupItem[] {
        return this.lookupFiltersHandler.ports;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.vesselTypeGroups')
    get vesselTypeGroups(): MepLookupItem[] {
        return this.lookupFiltersHandler.vesselTypeGroups;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.scopes')
    get scopes(): MepLookupItem[] {
        return this.lookupFiltersHandler.scopes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.operationModes')
    get operationModes(): MepLookupItem[] {
        return this.lookupFiltersHandler.operationModes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.financialYears')
    get years(): MepLookupItem[] {
        return this.lookupFiltersHandler.financialYears;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.isLookupDataRefreshing')
    get isLookupDataRefreshing(): boolean {
        return this.lookupFiltersHandler.isLookupDataRefreshing;
    }
    
    @computedFrom('isLookupDataRefreshing', 'financialYear')
    get yearPlaceholder(): string {

        if(this.selectedYear == null) {
            return `FY ${this.financialYear}`;
        } else {
            this.selectedYear;
        }
    }

    @computedFrom("isRefreshingData")
    get emissionTypeCategoryIds(): number[] {
        return Enumerable.from(this.selectedEmissionTypes.length == 0
            ? this.emissionTypes
            : this.selectedEmissionTypes)
            .where(x => x.additionalData != null)
            .select(x => x.additionalData.emissionTypeCategoryId)
            .distinct()
            .toArray();
    }
    
    constructor(refreshDataFunc: (request) => Promise<string[]>,
        mepLookupService: MepLookupService,
        customerId: number, private notificationClientService: NotificationClientService) {
        this.isRefreshingData = false;
        this.refreshDataFunc = refreshDataFunc;

        this.applyFiltersFunc = () => this.applyAllFilters();

        this.lookupFiltersHandler = new MepLookupFiltersHandler(null, this.applyAllFilters, mepLookupService, customerId);
        this.lookupFiltersHandler.includePortsLookup = true;
    }

    initLookups(customerId: number) {
        return this.lookupFiltersHandler.initLookupsByCustomer([
            Lookups.EmissionTypes,
            Lookups.Ports,
            Lookups.VesselTypeGroups,
            Lookups.Scopes,
            Lookups.OperationModes,
            Lookups.FinancialYears
        ], customerId);
    }

    applyAllFilters(isInit: boolean = false) {
        if (this.isRefreshingData || this.isLookupDataRefreshing)
            return;

        if (this.refreshDataFunc) {
            this.isRefreshingData = true;
            
            if(this.selectedYear == null) {
                this.financialYear = this.setInitYear()
            } else {
                this.financialYear = this.selectedYear;
            }

            if (isInit) {
                this.selectedPorts = this.ports;
                this.selectedEmissionTypes = this.emissionTypes;
                this.selectedVesselTypeGroups = this.vesselTypeGroups;
                this.selectedScopes = this.scopes;
                this.selectedOperationModes = this.operationModes;
            }
            
            this.refreshDataFunc(this.getRequest()).then(() => {
                this.isRefreshingData = false;
            }).catch(e => {
                this.isRefreshingData = false;
                if (typeof e === 'string') {
                    this.notificationClientService.show("MEP Error", e, NotificationType.Error);
                }
                else {
                    this.notificationClientService.show("MEP Error", "Error refreshing data", NotificationType.Error);
                }
            });
        }
    }

    //Set the initial year
    setInitYear() {
        
        //set to the default year / the last year in the list (if no default is set)
        const getYear = this.years && this.years.filter(x => x.isDefault).length > 0 ? this.years.filter(x => x.isDefault)[0] : this.years[this.years?.length - 1];
        return getYear.id;
    }

    //Get the seleceted filters - this is the request param for the refreshDataFunc
    getRequest() {
        // Need to pass all port ids as per: https://rightship.atlassian.net/browse/DEL-16873
        const portIds = this.selectedPorts.map(s => s.id);
        
        const vesselTypeGroupIds = this.selectedVesselTypeGroups.length == this.vesselTypeGroups.length 
            ? null 
            : this.selectedVesselTypeGroups.map(s => s.additionalData['vesselTypeGroupIds'].split('-').map(i => parseInt(i))).reduce((a, b) => a.concat(b), []);

        const request: BaseMepSummaryRequest = {
            year: this.financialYear,
            portIds: portIds,
            vesselTypeGroupIds: vesselTypeGroupIds,
            operationModeIds: MepLookupItem.getIdsFromLookups(this.operationModes, this.selectedOperationModes),
            emissionTypeIds: MepLookupItem.getIdsFromLookups(this.emissionTypes, this.selectedEmissionTypes),
            scopeIds: MepLookupItem.getIdsFromLookups(this.scopes, this.selectedScopes)
        };

        return request
    }

    resetAllFilters() {
        this.selectedPorts = this.ports;
        this.selectedEmissionTypes = this.emissionTypes;
        this.selectedVesselTypeGroups = this.vesselTypeGroups;
        this.selectedScopes = this.scopes;
        this.selectedOperationModes = this.operationModes;
        this.financialYear = this.setInitYear();
    }

    resetFiltersToDefault() {
        this.resetAllFilters();
        this.applyAllFilters();
    }

    changeEmissionTypesFilter(selectedEmissionTypeIds: number[]) {
        this.selectedEmissionTypes = MepLookupItem.getSelectedItems(this.emissionTypes, selectedEmissionTypeIds);
    }
}